import { Drawer, FormControlLabel, Grid, Switch, TextField } from '@chronosphereio/chrono-ui';
import { useCallback, useState } from 'react';
import { useImmer } from 'use-immer';
import { DiscardChangesConfirmationDialog } from '../../../components';
import { EventDefinition } from '../../../core';
import { Action, PluginEditor } from '../../../plugin-system';

interface EventEditFormProps {
  initialEventDefinition: EventDefinition;
  onChange: (def: EventDefinition) => void;
  onCancel: () => void;
  action?: Action;
}

export function EventEditForm(props: EventEditFormProps) {
  const { initialEventDefinition, onChange, onCancel, action = 'update' } = props;
  const [state, setState] = useImmer(initialEventDefinition);
  const [isDiscardDialogStateOpened, setDiscardDialogStateOpened] = useState<boolean>(false);

  // When user click on cancel, several possibilities:
  // - create action: ask for discard approval
  // - update action: ask for discard approval if changed
  const handleCancel = useCallback(() => {
    if (
      action === 'create' ||
      (action === 'update' && JSON.stringify(initialEventDefinition) !== JSON.stringify(state))
    ) {
      setDiscardDialogStateOpened(true);
    } else {
      onCancel();
    }
  }, [state, initialEventDefinition, action, setDiscardDialogStateOpened, onCancel]);

  return (
    <>
      <Drawer.Header
        title={action === 'create' ? 'Add event' : 'Edit event'}
        primaryButtonText={action === 'create' ? 'Add' : 'Apply'}
        onPrimaryButtonClick={() => onChange(state)}
        onSecondaryButtonClick={handleCancel}
      />
      <Drawer.Content>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={8}>
            <TextField
              required
              fullWidth
              label="Name"
              value={state.spec.name}
              onChange={(v) => {
                setState((draft) => {
                  draft.spec.name = v.target.value;
                });
              }}
            />
          </Grid>
          <Grid item xs={8}>
            <FormControlLabel
              control={
                <Switch
                  checked={state.spec.enabled !== false}
                  onChange={(e) => {
                    setState((draft) => {
                      draft.spec.enabled = e.target.checked;
                    });
                  }}
                />
              }
              label="Enable"
            />
          </Grid>

          <Grid item xs={8}>
            <PluginEditor
              width="100%"
              pluginType="EventQuery"
              pluginKindLabel="Datasource"
              value={state.spec.plugin}
              onChange={(val) => {
                setState((draft) => {
                  draft.spec.plugin = val;
                });
              }}
            />
          </Grid>
        </Grid>
      </Drawer.Content>

      <DiscardChangesConfirmationDialog
        description="Are you sure you want to discard these changes? Changes cannot be recovered."
        isOpen={isDiscardDialogStateOpened}
        onCancel={() => {
          setDiscardDialogStateOpened(false);
        }}
        onDiscardChanges={() => {
          setDiscardDialogStateOpened(false);
          onCancel();
        }}
      />
    </>
  );
}
